import "bootstrap/dist/css/bootstrap.min.css";
import React, { useState, useEffect, useRef } from "react";
import BassBoost from "../Effects/BassBoost/BassBoost";
import Mog from "../Effects/Mog/Mog";
import DownloadButton from "../DownloadButton/DownloadButton";
import UploadButton from "../UploadButton/UploadButton";

import styles from "./AudioPlayer.module.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faPause,
  faVolumeUp,
  faVolumeMute,
  faRepeat,
} from "@fortawesome/free-solid-svg-icons";

const AudioPlayer = ({ audioSrc }) => {
  // State variables
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [playbackRate, setPlaybackRate] = useState(1.2);
  const [audioBuffer, setAudioBuffer] = useState(null);
  const [reverbAmount, setReverbAmount] = useState(0.15);
  const [bassBoost, setBassBoost] = useState(0);
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [isLooping, setIsLooping] = useState(false);
  const [isMogging, setIsMogging] = useState(false);

  // Refs
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const gainNodeRef = useRef(null);
  const reverbNodeRef = useRef(null);
  const dryGainRef = useRef(null);
  const wetGainRef = useRef(null);
  const startTimeRef = useRef(0);
  const pausedAtRef = useRef(0);
  const progressTimerRef = useRef(null);
  const highPassFilterNodeRef = useRef(null);
  const lowPassFilterNodeRef = useRef(null);
  const bassBoostNodeRef = useRef(null);
  const playbackRateRef = useRef(1.4);
  const displayedProgressRef = useRef(0);
  const seekAnimFrameRef = useRef(null);
  const isSeekAnimatingRef = useRef(false);
  const chorusNodesRef = useRef([]);
  const chorusGainsRef = useRef([]);
  const impulseBufferRef = useRef(null);
  const isLoopingRef = useRef(false);
  const mogGainRef = useRef(null);
  const mogPostGainRef = useRef(null);
  const clipperRef = useRef(null);
  const clipCurveRef = useRef(null);
  const glitterCanvasRef = useRef(null);
  const glitterParticlesRef = useRef([]);
  const glitterAnimRef = useRef(null);

  // Initialize Web Audio API
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    audioContextRef.current = new AudioContext();

    gainNodeRef.current = audioContextRef.current.createGain();
    reverbNodeRef.current = audioContextRef.current.createConvolver();
    dryGainRef.current = audioContextRef.current.createGain();
    wetGainRef.current = audioContextRef.current.createGain();

    gainNodeRef.current.gain.value = volume;
    dryGainRef.current.gain.value = 1 - reverbAmount;
    wetGainRef.current.gain.value = reverbAmount;

    createLowPassFilter();
    createHighPassFilter();
    createReverbImpulse();
    createChorus();

    bassBoostNodeRef.current = audioContextRef.current.createBiquadFilter();
    bassBoostNodeRef.current.type = "lowshelf";
    bassBoostNodeRef.current.frequency.value = 100;
    bassBoostNodeRef.current.gain.value = 0;

    mogGainRef.current = audioContextRef.current.createGain();
    mogGainRef.current.gain.value = 1.0;

    clipperRef.current = audioContextRef.current.createWaveShaper();
    const clipN = 256;
    const clipCurve = new Float32Array(clipN);
    for (let i = 0; i < clipN; i++) {
      const x = (i * 2) / (clipN - 1) - 1;
      clipCurve[i] = Math.max(-1, Math.min(1, x));
    }
    clipCurveRef.current = clipCurve;
    clipperRef.current.curve = null; // off by default
    clipperRef.current.oversample = "4x";

    mogPostGainRef.current = audioContextRef.current.createGain();
    mogPostGainRef.current.gain.value = 1.0;

    gainNodeRef.current.connect(bassBoostNodeRef.current);
    bassBoostNodeRef.current.connect(mogGainRef.current);
    mogGainRef.current.connect(clipperRef.current);
    clipperRef.current.connect(mogPostGainRef.current);
    mogPostGainRef.current.connect(audioContextRef.current.destination);

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Function to normalize an AudioBuffer's channel data
  const normalizeBuffer = (channelData) => {
    let maxVal = 0;

    // Find max absolute value
    for (let i = 0; i < channelData.length; i++) {
      maxVal = Math.max(maxVal, Math.abs(channelData[i]));
    }

    // Normalize if maxVal is greater than zero
    if (maxVal > 0) {
      for (let i = 0; i < channelData.length; i++) {
        channelData[i] /= maxVal;
      }
    }
  };

  const createLowPassFilter = () => {
    lowPassFilterNodeRef.current = audioContextRef.current.createBiquadFilter();
    lowPassFilterNodeRef.current.type = "lowpass";
    lowPassFilterNodeRef.current.frequency.value = 10000; // Set cutoff frequency
    lowPassFilterNodeRef.current.Q.value = 0.707; // Q factor for 6dB/oct slope
  };

  const createHighPassFilter = () => {
    highPassFilterNodeRef.current =
      audioContextRef.current.createBiquadFilter();
    highPassFilterNodeRef.current.type = "highpass";
    highPassFilterNodeRef.current.frequency.value = 200; // Set cutoff frequency
    highPassFilterNodeRef.current.Q.value = 0.707; // Q factor for 
  };

  const createChorus = () => {
    const chorusNodes = [];
    const numChorus = 3; // Number of chorus copies

    // Create multiple delay nodes to simulate chorus effect
    for (let i = 0; i < numChorus; i++) {
      const delayNode = audioContextRef.current.createDelay();
      delayNode.delayTime.value = 0.03 * (i + 1) + Math.random() * 0.005; // Increased range with slight randomness
      chorusNodes.push(delayNode);
    }

    // Create gain nodes for each chorus voice
    const chorusGains = [];
    for (let i = 0; i < numChorus; i++) {
      const gainNode = audioContextRef.current.createGain();
      gainNode.gain.value = 0.6; // Increased gain for more depth
      chorusGains.push(gainNode);
    }

    // Add modulation to the delay times for depth
    for (let i = 0; i < numChorus; i++) {
      const modulator = audioContextRef.current.createOscillator();
      modulator.frequency.value = 0.25; // Slower modulation for deeper effect
      modulator.type = "sine";
      modulator.start();

      const modulatorGain = audioContextRef.current.createGain();
      modulatorGain.gain.value = 0.002; // Depth of modulation
      modulator.connect(modulatorGain);
      modulatorGain.connect(chorusNodes[i].delayTime);
    }

    // Connect each delay node to its gain (output will be routed in play())
    for (let i = 0; i < numChorus; i++) {
      chorusNodes[i].connect(chorusGains[i]);
    }

    // Store nodes for use in play() and cleanup
    chorusNodesRef.current = chorusNodes;
    chorusGainsRef.current = chorusGains;
  };

  // Create hall-style reverb impulse response
  const createReverbImpulse = async () => {
    if (!audioContextRef.current || !reverbNodeRef.current) return;

    const sampleRate = audioContextRef.current.sampleRate;
    const reverbTime = 6; // seconds
    const length = reverbTime * sampleRate;
    const impulse = audioContextRef.current.createBuffer(2, length, sampleRate);
    const leftChannel = impulse.getChannelData(0);
    const rightChannel = impulse.getChannelData(1);

    // Pre-delay: ~20ms of silence before first reflection
    const preDelaySamples = Math.floor(0.02 * sampleRate);

    // Discrete early reflections (time in seconds, gain, pan L/R offset in samples)
    const earlyTaps = [
      { time: 0.021, gain: 0.8,  offsetL: 0,  offsetR: 3  },
      { time: 0.033, gain: 0.65, offsetL: 2,  offsetR: 0  },
      { time: 0.048, gain: 0.5,  offsetL: 0,  offsetR: 5  },
      { time: 0.062, gain: 0.4,  offsetL: 4,  offsetR: 0  },
      { time: 0.078, gain: 0.3,  offsetL: 0,  offsetR: 2  },
      { time: 0.095, gain: 0.22, offsetL: 3,  offsetR: 0  },
      { time: 0.118, gain: 0.15, offsetL: 0,  offsetR: 4  },
      { time: 0.145, gain: 0.1,  offsetL: 1,  offsetR: 0  },
    ];

    // Place discrete early reflections
    for (const tap of earlyTaps) {
      const baseSample = Math.floor(tap.time * sampleRate);
      const idxL = baseSample + tap.offsetL;
      const idxR = baseSample + tap.offsetR;
      if (idxL < length) leftChannel[idxL] = tap.gain;
      if (idxR < length) rightChannel[idxR] = tap.gain * 0.9;
    }

    // Late diffuse tail with frequency-dependent decay and density buildup
    const tailStart = Math.floor(0.08 * sampleRate);
    // Simple one-pole low-pass state for warming the tail
    let lpStateL = 0;
    let lpStateR = 0;

    for (let i = Math.max(preDelaySamples, tailStart); i < length; i++) {
      const t = i / sampleRate; // time in seconds
      const n = i / length;     // normalized position

      // Frequency-dependent decay: lows decay slower than highs
      const lowDecay = Math.exp(-1.5 * n);   // slow decay for low frequencies
      const highDecay = Math.exp(-5.0 * n);  // fast decay for high frequencies
      // Blend: as time progresses, high-frequency content fades faster
      const envelope = 0.6 * lowDecay + 0.4 * highDecay;

      // Density buildup: noise amplitude ramps up during early tail
      const density = Math.min(1.0, (t - 0.08) / 0.12);

      // Generate noise
      const noiseL = (Math.random() * 2 - 1) * envelope * density;
      const noiseR = (Math.random() * 2 - 1) * envelope * density;

      // One-pole low-pass to darken the tail over time
      // Coefficient increases (more filtering) as time progresses
      const lpCoeff = 0.2 + 0.6 * n; // 0.2 early → 0.8 late
      lpStateL = lpStateL + lpCoeff * (noiseL - lpStateL);
      lpStateR = lpStateR + lpCoeff * (noiseR - lpStateR);

      leftChannel[i] += lpStateL;
      rightChannel[i] += lpStateR;
    }

    normalizeBuffer(leftChannel);
    normalizeBuffer(rightChannel);

    impulseBufferRef.current = impulse;
    reverbNodeRef.current.buffer = impulse;
  };

  // Update reverb wet/dry mix
  useEffect(() => {
    if (dryGainRef.current && wetGainRef.current) {
      dryGainRef.current.gain.value = 1 - reverbAmount;
      wetGainRef.current.gain.value = reverbAmount;
    }
  }, [reverbAmount]);

  // Update bass boost gain
  useEffect(() => {
    if (bassBoostNodeRef.current) {
      bassBoostNodeRef.current.gain.value = bassBoost;
    }
  }, [bassBoost]);

  // Load the audio file
  useEffect(() => {
    const loadAudio = async (source) => {
      if (!source) return;

      try {
        let arrayBuffer;

        if (typeof source === "string") {
          const response = await fetch(source);
          arrayBuffer = await response.arrayBuffer();
        } else {
          arrayBuffer = source;
        }

        const decodedBuffer = await audioContextRef.current.decodeAudioData(
          arrayBuffer
        );
        setAudioBuffer(decodedBuffer);
        setDuration(decodedBuffer.duration);
        pausedAtRef.current = 0;
        setCurrentTime(0);
      } catch (error) {
        console.error("Error loading audio:", error);
      }
    };

    if (audioContextRef.current) {
      // Stop any currently playing audio before loading new file
      cleanupAudio();
      setIsPlaying(false);
      setDisplayedProgress(0);
      displayedProgressRef.current = 0;

      if (uploadedFile) {
        loadAudio(uploadedFile);
      } else if (audioSrc) {
        loadAudio(audioSrc);
      }
    }

    return () => {
      cleanupAudio();
    };
  }, [audioSrc, uploadedFile]);

  const handleFileLoad = (arrayBuffer, fileName) => {
    setUploadedFile(arrayBuffer);
    setUploadedFileName(fileName);
  };

  // Helper to safely clean up audio resources
  const cleanupAudio = () => {
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.stop();
      } catch (e) {}

      try {
        sourceNodeRef.current.disconnect();
      } catch (e) {}

      sourceNodeRef.current = null;
    }

    // Disconnect chorus nodes and gains
    chorusNodesRef.current.forEach((node) => {
      try { node.disconnect(); } catch (e) {}
    });
    chorusGainsRef.current.forEach((node) => {
      try { node.disconnect(); } catch (e) {}
    });

    if (dryGainRef.current) {
      try {
        dryGainRef.current.disconnect();
      } catch (e) {}
    }

    if (wetGainRef.current) {
      try {
        wetGainRef.current.disconnect();
      } catch (e) {}
    }

    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };


  const play = (position) => {
    if (!audioBuffer) return;

    try {
      cleanupAudio();

      if (audioContextRef.current.state === "suspended") {
        audioContextRef.current.resume();
      }

      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.playbackRate.value = playbackRateRef.current;

      const dryGain = audioContextRef.current.createGain();
      const wetGain = audioContextRef.current.createGain();

      dryGain.gain.value = 1 - reverbAmount;
      wetGain.gain.value = reverbAmount;

      // Dry signal connects to dry gain which goes to the final output
      source.connect(dryGain);
      dryGain.connect(gainNodeRef.current);

      // Wet signal: source → wetGain → chorus → highPass → reverb → lowPass → master
      source.connect(wetGain);
      wetGain.connect(chorusNodesRef.current[0]);
      for (let i = 0; i < chorusNodesRef.current.length - 1; i++) {
        chorusNodesRef.current[i].connect(chorusNodesRef.current[i + 1]);
      }
      // Re-establish node→gain connections (severed by cleanupAudio)
      for (let i = 0; i < chorusNodesRef.current.length; i++) {
        chorusNodesRef.current[i].connect(chorusGainsRef.current[i]);
      }
      for (let i = 0; i < chorusGainsRef.current.length; i++) {
        chorusGainsRef.current[i].connect(highPassFilterNodeRef.current);
      }
      highPassFilterNodeRef.current.connect(reverbNodeRef.current);
      reverbNodeRef.current.connect(lowPassFilterNodeRef.current);
      lowPassFilterNodeRef.current.connect(gainNodeRef.current);

      sourceNodeRef.current = source;
      dryGainRef.current = dryGain;
      wetGainRef.current = wetGain;

      // Normalize the final output
      // const normalizationFactor = normalizeAudioBuffer(audioBuffer);
      // gainNodeRef.current.gain.value = normalizationFactor;

      const validPosition = Math.max(
        0,
        Math.min(position, audioBuffer.duration - 0.01)
      );

      source.start(0, validPosition);
      startTimeRef.current =
        audioContextRef.current.currentTime - validPosition / playbackRateRef.current;
      pausedAtRef.current = validPosition;

      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }

      progressTimerRef.current = setInterval(() => {
        if (sourceNodeRef.current) {
          const elapsed =
            (audioContextRef.current.currentTime - startTimeRef.current) *
            playbackRateRef.current;
          if (elapsed >= audioBuffer.duration) {
            if (isLoopingRef.current) {
              cleanupAudio();
              setCurrentTime(0);
              setDisplayedProgress(0);
              displayedProgressRef.current = 0;
              pausedAtRef.current = 0;
              play(0);
            } else {
              cleanupAudio();
              setCurrentTime(0);
              setDisplayedProgress(0);
              displayedProgressRef.current = 0;
              setIsPlaying(false);
              pausedAtRef.current = 0;
            }
          } else {
            setCurrentTime(elapsed);
            if (!isSeekAnimatingRef.current) {
              const pct = (elapsed / audioBuffer.duration) * 100;
              setDisplayedProgress(pct);
              displayedProgressRef.current = pct;
            }
          }
        }
      }, 15);

      source.onended = () => {
        if (sourceNodeRef.current === source) {
          if (isLoopingRef.current) {
            cleanupAudio();
            pausedAtRef.current = 0;
            play(0);
          } else {
            cleanupAudio();
            setCurrentTime(0);
            setIsPlaying(false);
            pausedAtRef.current = 0;
          }
        }
      };

      setIsPlaying(true);
    } catch (error) {
      console.error("Error playing audio:", error);
      setIsPlaying(false);
    }
  };

  // Pause playback
  const pause = () => {
    if (!isPlaying) return;

    pausedAtRef.current = currentTime;
    cleanupAudio();
    setIsPlaying(false);
  };

  // Toggle MOG (+20dB into hard clipper, -20dB out)
  const toggleMog = () => {
    setIsMogging((prev) => !prev);
  };

  useEffect(() => {
    if (mogGainRef.current && clipperRef.current && mogPostGainRef.current) {
      mogGainRef.current.gain.value = isMogging ? 10.0 : 1.0;
      clipperRef.current.curve = isMogging ? clipCurveRef.current : null;
      mogPostGainRef.current.gain.value = isMogging ? 0.1 : 1.0;
    }
  }, [isMogging]);

  // Toggle loop
  const toggleLoop = () => {
    setIsLooping((prev) => {
      isLoopingRef.current = !prev;
      return !prev;
    });
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    if (isPlaying) {
      pause();
    } else {
      play(pausedAtRef.current);
    }
  };

  useEffect(() => {
    const handleSpacebarPress = (event) => {
      if (event.keyCode === 32) {
        event.preventDefault();
        togglePlayPause();
      }
    };

    window.addEventListener("keydown", handleSpacebarPress);
    return () => window.removeEventListener("keydown", handleSpacebarPress);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [togglePlayPause]);

  // Handle seeking
  const handleSeek = (e) => {
    if (!audioBuffer) return;

    const progressBar = e.currentTarget;
    const bounds = progressBar.getBoundingClientRect();
    const x = Math.max(0, Math.min(bounds.width, e.clientX - bounds.left));
    const percentage = x / bounds.width;
    const seekTime = percentage * duration;

    setCurrentTime(seekTime);
    pausedAtRef.current = seekTime;

    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }

    const fromProgress = displayedProgressRef.current;
    const toProgress = percentage * 100;
    const animStart = performance.now();
    const animDuration = 500;

    cancelAnimationFrame(seekAnimFrameRef.current);
    isSeekAnimatingRef.current = true;

    const animate = (now) => {
      const t = Math.min((now - animStart) / animDuration, 1);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      const pct = fromProgress + (toProgress - fromProgress) * eased;
      displayedProgressRef.current = pct;
      setDisplayedProgress(pct);
      if (t < 1) {
        seekAnimFrameRef.current = requestAnimationFrame(animate);
      } else {
        isSeekAnimatingRef.current = false;
      }
    };
    seekAnimFrameRef.current = requestAnimationFrame(animate);

    if (isPlaying) {
      setTimeout(() => play(seekTime), 50);
    }
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const value = parseFloat(e.target.value);
    setVolume(value);

    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = value;
    }
  };

  // Handle reverb amount change
  const handleReverbChange = (e) => {
    const amount = parseFloat(e.target.value);
    setReverbAmount(amount);
  };

  // Handle bass boost change
  const handleBassBoostChange = (db) => {
    setBassBoost(db);
  };

  // Handle playback rate change
  const handlePlaybackRateChange = (e) => {
    const rate = parseFloat(e.target.value);
    setPlaybackRate(rate);

    if (sourceNodeRef.current && isPlaying) {
      try {
        const currentPos =
          (audioContextRef.current.currentTime - startTimeRef.current) *
          playbackRateRef.current;
        sourceNodeRef.current.playbackRate.value = rate;
        playbackRateRef.current = rate;
        startTimeRef.current =
          audioContextRef.current.currentTime - currentPos / rate;
      } catch (error) {
        const currentPosition = currentTime;
        cleanupAudio();
        setTimeout(() => play(currentPosition), 50);
      }
    }
  };

  // Format time display
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Carbonated glitter particles on progress bar
  useEffect(() => {
    const canvas = glitterCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const particles = glitterParticlesRef.current;
    const dpr = window.devicePixelRatio || 1;

    const spawn = (w, h) => {
      particles.push({
        x: Math.random() * w,
        y: h + Math.random() * 2,
        vx: (Math.random() - 0.5) * 0.1,
        vy: -(0.03 + Math.random() * 0.07),
        size: 0.2 + Math.random() * 0.35,
        life: 0,
        maxLife: 50 + Math.random() * 60,
        brightness: 0.5 + Math.random() * 0.5,
        twinkleSpeed: 5 + Math.random() * 10,
      });
    };

    let running = true;
    const loop = () => {
      if (!running) return;
      const rect = canvas.parentElement.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        canvas.style.width = w + "px";
        canvas.style.height = h + "px";
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }

      ctx.clearRect(0, 0, w, h);

      // Spawn ~3-5 particles per frame for fizzy density
      const count = 3 + Math.floor(Math.random() * 3);
      for (let i = 0; i < count; i++) spawn(w, h);

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vx += (Math.random() - 0.5) * 0.03; // fizzy wobble
        p.life++;

        const lifeRatio = p.life / p.maxLife;
        // Fade in quickly, fade out at end
        let alpha = lifeRatio < 0.15 ? lifeRatio / 0.15 : 1 - ((lifeRatio - 0.15) / 0.85);
        // Gentle shimmer
        alpha *= 0.75 + 0.25 * Math.sin(p.life * p.twinkleSpeed * 0.04);
        alpha *= p.brightness;
        alpha = Math.max(0, Math.min(1, alpha));

        if (p.life >= p.maxLife || p.y < -2) {
          particles.splice(i, 1);
          continue;
        }

        // Draw sparkle: bright white core + soft glow
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = "#fff";
        ctx.shadowColor = "rgba(255,255,255,0.8)";
        ctx.shadowBlur = p.size * 2;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        // Cross sparkle for larger particles
        if (p.size > 0.8) {
          ctx.globalAlpha = alpha * 0.6;
          ctx.strokeStyle = "#fff";
          ctx.lineWidth = 0.4;
          const arm = p.size * 1.8;
          ctx.beginPath();
          ctx.moveTo(p.x - arm, p.y);
          ctx.lineTo(p.x + arm, p.y);
          ctx.moveTo(p.x, p.y - arm);
          ctx.lineTo(p.x, p.y + arm);
          ctx.stroke();
        }
        ctx.restore();
      }

      glitterAnimRef.current = requestAnimationFrame(loop);
    };

    glitterAnimRef.current = requestAnimationFrame(loop);
    return () => {
      running = false;
      cancelAnimationFrame(glitterAnimRef.current);
      particles.length = 0;
    };
  }, []);

  const speedPct = ((playbackRate - 0.1) / 1.9) * 100;

  return (
    <div className={styles.container}>
      <UploadButton onFileLoad={handleFileLoad} styles={styles} />

      <div className={styles.card}>
        {/* Track header */}
        <div className={styles.trackHeader}>
          <span className={`${styles.trackName} ${uploadedFileName ? styles.trackNameLoaded : ""}`}>
            {uploadedFileName
              ? uploadedFileName.replace(/\.[^/.]+$/, "").toUpperCase()
              : "NO FILE LOADED"}
          </span>
          <span className={styles.trackTime}>
            {formatTime(currentTime)}
            <span className={styles.timeSep}>/</span>
            {formatTime(duration)}
          </span>
        </div>

        {/* Progress bar */}
        <div className={styles.progressBar} onClick={handleSeek}>
          <div
            className={styles.progressFill}
            style={{ width: `${displayedProgress}%` }}
          >
            <canvas ref={glitterCanvasRef} className={styles.glitterCanvas} />
          </div>
          <div
            className={styles.progressKnob}
            style={{ left: `${displayedProgress}%` }}
          />
        </div>

        {/* Transport */}
        <div className={styles.transport}>
          <button
            onClick={togglePlayPause}
            className={`${styles.playButton} ${isPlaying ? styles.playButtonActive : ""}`}
            disabled={!audioBuffer}
          >
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </button>

          <button
            onClick={toggleLoop}
            className={`${styles.loopButton} ${isLooping ? styles.loopButtonActive : ""}`}
          >
            <FontAwesomeIcon icon={faRepeat} />
          </button>

          <div className={styles.volumeWrap}>
            <FontAwesomeIcon icon={faVolumeMute} className={styles.volIcon} />
            <div className={styles.sliderWrap}>
              <div className={styles.sliderTrack}>
                <div className={styles.sliderFill} style={{ width: `${volume * 100}%` }} />
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.001"
                value={volume}
                onChange={handleVolumeChange}
                onDoubleClick={() => handleVolumeChange({ target: { value: "0.5" } })}
                className={styles.sliderInput}
              />
              <div className={styles.sliderKnob} style={{ left: `${volume * 100}%` }} />
            </div>
            <FontAwesomeIcon icon={faVolumeUp} className={styles.volIcon} />
          </div>
        </div>

        <div className={styles.divider} />

        {/* Effects */}
        <div className={styles.effects}>
          {/* Speed */}
          <div className={styles.effectRow}>
            <div className={styles.effectMeta}>
              <span className={styles.effectLabel}>Speed</span>
              <span className={styles.effectValue}>
                <input
                  type="text"
                  className={styles.effectInput}
                  defaultValue={playbackRate.toFixed(2)}
                  key={`speed-${playbackRate}`}
                  onFocus={(e) => e.target.select()}
                  onBlur={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v)) {
                      const clamped = Math.min(2, Math.max(0.1, v));
                      handlePlaybackRateChange({ target: { value: String(clamped) } });
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                />×
              </span>
            </div>
            <div className={styles.sliderWrap}>
              <div className={styles.sliderTrack}>
                <div className={styles.sliderFill} style={{ width: `${speedPct}%` }} />
              </div>
              <input
                type="range"
                min="0.1"
                max="2.0"
                step="0.01"
                value={playbackRate}
                onChange={handlePlaybackRateChange}
                onDoubleClick={() => handlePlaybackRateChange({ target: { value: "1" } })}
                className={styles.sliderInput}
              />
              <div className={styles.sliderKnob} style={{ left: `${speedPct}%` }} />
            </div>
          </div>

          {/* Reverb */}
          <div className={styles.effectRow}>
            <div className={styles.effectMeta}>
              <span className={styles.effectLabel}>Reverb</span>
              <span className={styles.effectValue}>
                <input
                  type="text"
                  className={styles.effectInput}
                  defaultValue={Math.round(reverbAmount * 100)}
                  key={`reverb-${reverbAmount}`}
                  onFocus={(e) => e.target.select()}
                  onBlur={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v)) {
                      const clamped = Math.min(100, Math.max(0, v)) / 100;
                      handleReverbChange({ target: { value: String(clamped) } });
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                />%
              </span>
            </div>
            <div className={styles.sliderWrap}>
              <div className={styles.sliderTrack}>
                <div className={styles.sliderFill} style={{ width: `${reverbAmount * 100}%` }} />
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={reverbAmount}
                onChange={handleReverbChange}
                onDoubleClick={() => handleReverbChange({ target: { value: "0" } })}
                className={styles.sliderInput}
              />
              <div className={styles.sliderKnob} style={{ left: `${reverbAmount * 100}%` }} />
            </div>
          </div>

          {/* Bass */}
          <BassBoost
            bassBoost={bassBoost}
            handleBassBoostChange={handleBassBoostChange}
            styles={styles}
          />

          <Mog isMogging={isMogging} toggleMog={toggleMog} styles={styles} />
        </div>

        <DownloadButton
          audioBuffer={audioBuffer}
          playbackRate={playbackRate}
          reverbAmount={reverbAmount}
          bassBoost={bassBoost}
          volume={volume}
          impulseBuffer={impulseBufferRef.current}
          fileName={uploadedFileName}
          styles={styles}
        />
      </div>
    </div>
  );
};

export default AudioPlayer;
